import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { userService, FullUserProfile } from "../services/userService";
import { apiService } from "../services/apiService";
import PageHeader from "../components/common/PageHeader";
import { useToast, ToastContainer } from "../components/common/Toast";
import { useAuth } from "../contexts/AuthContext";
import { useSubscription } from "../hooks/useSubscription";
import ApiKeyUpgradeModal from "../components/common/ApiKeyUpgradeModal";

// Reusable chevron right icon component
const ChevronRight: React.FC = () => (
  <svg
    className="w-5 h-5 text-gray-500"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 5l7 7-7 7"
    />
  </svg>
);

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const { canUseCustomApiKeys } = useSubscription();
  const { toasts, removeToast, success: showSuccess, error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<FullUserProfile | null>(null);
  const [showApiKeyUpgradeModal, setShowApiKeyUpgradeModal] = useState(false);

  const [form, setForm] = useState({
    timezone: "UTC",
    preferred_language: "en",
    email_notifications: true,
  });

  // LLM configuration state
  const [llmPreferredProvider, setLlmPreferredProvider] = useState<
    "openai" | "anthropic" | ""
  >("");
  const [hasOpenAIKey, setHasOpenAIKey] = useState(false);
  const [hasAnthropicKey, setHasAnthropicKey] = useState(false);
  const [openaiApiKey, setOpenaiApiKey] = useState("");
  const [anthropicApiKey, setAnthropicApiKey] = useState("");
  const [savingLlm, setSavingLlm] = useState(false);

  // Contact Support form state
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const [referralSource, setReferralSource] = useState("");
  const [recommendationRating, setRecommendationRating] = useState(5);
  const [improvements, setImprovements] = useState("");
  const [additionalDetails, setAdditionalDetails] = useState("");
  const [contactFormLoading, setContactFormLoading] = useState(false);

  // Compute effective provider for validation if none explicitly selected
  const effectiveProvider: "openai" | "anthropic" =
    llmPreferredProvider || (hasAnthropicKey ? "anthropic" : "openai");

  // Validation flags for provider/key requirements
  const openaiNeedsKey =
    effectiveProvider === "openai" && !hasOpenAIKey && !openaiApiKey.trim();
  const anthropicNeedsKey =
    effectiveProvider === "anthropic" &&
    !hasAnthropicKey &&
    !anthropicApiKey.trim();

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await userService.getFullProfile();
        setProfile(data);
        setForm({
          timezone: data?.settings?.timezone || "UTC",
          preferred_language: data?.settings?.preferred_language || "en",
          email_notifications: data?.settings?.email_notifications ?? true,
        });

        // Load LLM configuration
        const llm = await apiService.getLlmConfig();
        const rawProvider = llm.data.preferredProvider;
        const initialProvider: "openai" | "anthropic" =
          (rawProvider === "openai" || rawProvider === "anthropic" ? rawProvider : null) ||
          (llm.data.hasAnthropicKey ? "anthropic" : "openai");
        setLlmPreferredProvider(initialProvider);
        setHasOpenAIKey(llm.data.hasOpenAIKey);
        setHasAnthropicKey(llm.data.hasAnthropicKey);
      } catch (err) {
        showError(
          "Failed to load settings",
          err instanceof Error ? err.message : undefined
        );
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSaveAll = async () => {
    // Validate LLM configuration before saving
    if (openaiNeedsKey || anthropicNeedsKey) {
      showError("Please provide the required API key for your selected LLM provider");
      return;
    }

    try {
      setSaving(true);
      
      // Save account settings
      await userService.updateSettings(form);
      
      // Save LLM settings if any keys were entered
      // preferredProvider is already auto-saved on change, only include keys here
      const llmPayload: {
        openaiApiKey?: string;
        anthropicApiKey?: string;
      } = {};
      if (openaiApiKey.trim()) llmPayload.openaiApiKey = openaiApiKey.trim();
      if (anthropicApiKey.trim()) llmPayload.anthropicApiKey = anthropicApiKey.trim();
      
      if (Object.keys(llmPayload).length > 0) {
        await apiService.updateLlmConfig(llmPayload);
        // Refresh config and clear sensitive inputs
        const llm = await apiService.getLlmConfig();
        const rawProvider = llm.data.preferredProvider;
        const refreshedProvider: "openai" | "anthropic" =
          (rawProvider === "openai" || rawProvider === "anthropic"
            ? rawProvider
            : null) ||
          (llm.data.hasAnthropicKey ? "anthropic" : "openai");
        setLlmPreferredProvider(refreshedProvider);
        setHasOpenAIKey(llm.data.hasOpenAIKey);
        setHasAnthropicKey(llm.data.hasAnthropicKey);
        setOpenaiApiKey("");
        setAnthropicApiKey("");
      }
      
      showSuccess("Settings saved successfully");
    } catch (err) {
      showError(
        "Failed to save settings",
        err instanceof Error ? err.message : undefined
      );
    } finally {
      setSaving(false);
    }
  };

  const handlePreferredProviderChange = async (value: "openai" | "anthropic") => {
    const previousValue = llmPreferredProvider;
    setLlmPreferredProvider(value);
    // Immediately persist preferred provider only
    try {
      setSavingLlm(true);
      await apiService.setPreferredProvider(value);
      showSuccess("Preferred provider updated");
    } catch (err) {
      setLlmPreferredProvider(previousValue); // Revert on failure
      showError(
        "Failed to update preferred provider",
        err instanceof Error ? err.message : undefined
      );
    } finally {
      setSavingLlm(false);
    }
  };

  const handleContactSupport = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactFormLoading(true);

    try {
      await apiService.post('/api/support/contact', {
        referralSource,
        recommendationRating,
        improvements,
        additionalDetails,
      });

      showSuccess('Thank you! Your message has been sent.');

      // Reset form
      setReferralSource("");
      setRecommendationRating(5);
      setImprovements("");
      setAdditionalDetails("");
      setContactFormOpen(false);
    } catch (error: any) {
      console.error('Error submitting contact form:', error);
      showError(error.response?.data?.error || 'Failed to send message');
    } finally {
      setContactFormLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-lg text-gray-400">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="w-full px-9 py-6">
      <PageHeader
        title="Settings"
        subtitle="Manage your preferences"
        action={
          <button
            onClick={handleSaveAll}
            disabled={saving || savingLlm}
            className="su-btn-primary"
          >
            {saving || savingLlm ? "SAVING..." : "SAVE SETTINGS"}
          </button>
        }
      />

      <div className="space-y-6 mt-6">
        {/* Quick Access - MOVED TO TOP */}
        <div className="su-section">
          <h2 className="su-text-section-heading mb-2">Quick Access</h2>
          <p className="text-sm text-gray-400 mb-4">
            Manage your connections and contact lists
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => navigate("/integrations")}
              className="flex items-center justify-between px-4 py-3 bg-black border-2 border-gray-700 text-white hover:border-purple-500 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-purple-900/30 border border-purple-500 flex items-center justify-center">
                  <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="text-left">
                  <div className="font-semibold text-gray-200">Integrations</div>
                  <div className="text-xs text-gray-500">Connect your tools</div>
                </div>
              </div>
              <ChevronRight />
            </button>

            <button
              onClick={() => navigate("/contacts")}
              className="flex items-center justify-between px-4 py-3 bg-black border-2 border-gray-700 text-white hover:border-purple-500 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-purple-900/30 border border-purple-500 flex items-center justify-center">
                  <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div className="text-left">
                  <div className="font-semibold text-gray-200">Contacts</div>
                  <div className="text-xs text-gray-500">Manage contact lists</div>
                </div>
              </div>
              <ChevronRight />
            </button>
          </div>
        </div>

        {/* Personal Information */}
        <div className="su-section">
          <h2 className="su-text-section-heading mb-4">Personal Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="su-profile-label">Full Name</label>
              <div className="su-profile-value">
                {profile?.profile.full_name || "Not specified"}
              </div>
            </div>
            <div>
              <label className="su-profile-label">Email Address</label>
              <div className="su-profile-value">
                {profile?.profile.email || ""}
              </div>
            </div>
          </div>
          <div className="mt-4">
            <button
              onClick={() => navigate("/profile")}
              className="su-btn-secondary"
            >
              GO TO PROFILE
            </button>
          </div>
        </div>

        {/* Account Settings */}
        <div className="su-section">
          <h2 className="su-text-section-heading mb-4">Account Settings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="su-profile-label">Timezone</label>
              <select
                value={form.timezone}
                onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                className="su-profile-input"
              >
                <option value="UTC">UTC</option>
                <option value="America/New_York">Eastern Time</option>
                <option value="America/Chicago">Central Time</option>
                <option value="America/Denver">Mountain Time</option>
                <option value="America/Los_Angeles">Pacific Time</option>
                <option value="Europe/London">London</option>
                <option value="Europe/Berlin">Berlin</option>
                <option value="Asia/Tokyo">Tokyo</option>
              </select>
            </div>
            <div>
              <label className="su-profile-label">Language</label>
              <select
                value={form.preferred_language}
                onChange={(e) =>
                  setForm({ ...form, preferred_language: e.target.value })
                }
                className="su-profile-input"
              >
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.email_notifications}
                  onChange={(e) =>
                    setForm({ ...form, email_notifications: e.target.checked })
                  }
                  className="mr-3 h-4 w-4 text-purple-500 focus:ring-purple-500 border-gray-700 bg-black"
                />
                <span className="su-profile-label">Email Notifications</span>
              </label>
              <p className="text-sm text-gray-500 ml-7 mt-1">
                Receive email notifications about account activity and updates
              </p>
            </div>
          </div>
        </div>

        {/* LLM Configuration */}
        <div className="su-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="su-text-section-heading">LLM Configuration</h2>
            {!canUseCustomApiKeys && (
              <span
                style={{
                  backgroundColor: "rgba(168, 85, 247, 0.2)",
                  border: "1px solid #A855F7",
                  color: "#A855F7",
                  fontSize: "10px",
                  fontWeight: "bold",
                  padding: "2px 8px",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Pro
              </span>
            )}
          </div>

          {canUseCustomApiKeys ? (
            <>
              <div className="mb-4 border border-blue-800 bg-blue-900/20 p-3 text-sm text-blue-300">
                For your security, we never display saved API keys. Enter a new key
                to set or replace it. Keys are encrypted and never stored in plain
                text.
              </div>
              <div className="space-y-6">
                <div>
                  <label className="su-profile-label block mb-2">Preferred Provider</label>
                  <select
                    value={llmPreferredProvider}
                    onChange={(e) => handlePreferredProviderChange(e.target.value as "openai" | "anthropic")}
                    disabled={savingLlm}
                    className="su-profile-input max-w-md"
                  >
                    <option value="anthropic">Claude (Anthropic)</option>
                    <option value="openai">OpenAI</option>
                  </select>
                  {openaiNeedsKey && (
                    <div className="mt-2 text-sm font-bold text-red-400">
                      OpenAI key required for selected provider
                    </div>
                  )}
                  {anthropicNeedsKey && (
                    <div className="mt-2 text-sm font-bold text-red-400">
                      Anthropic key required for selected provider
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="su-profile-label block mb-2">Claude (Anthropic) API Key</label>
                    <input
                      type="password"
                      autoComplete="off"
                      value={anthropicApiKey}
                      onChange={(e) => setAnthropicApiKey(e.target.value)}
                      placeholder={hasAnthropicKey ? "******** (key set)" : "sk-ant-..."}
                      aria-invalid={anthropicNeedsKey}
                      className={`su-profile-input ${
                        anthropicNeedsKey ? "border-red-800" : ""
                      }`}
                    />
                    <div className="mt-2 flex items-center text-xs text-gray-400">
                      <span
                        className={`mr-2 inline-block h-2 w-2 rounded-full ${
                          hasAnthropicKey ? "bg-green-500" : "bg-gray-600"
                        }`}
                        aria-hidden="true"
                      />
                      {hasAnthropicKey ? "Key on file (hidden)" : "No key on file"}
                    </div>
                  </div>

                  <div>
                    <label className="su-profile-label block mb-2">OpenAI API Key</label>
                    <input
                      type="password"
                      autoComplete="off"
                      value={openaiApiKey}
                      onChange={(e) => setOpenaiApiKey(e.target.value)}
                      placeholder={hasOpenAIKey ? "******** (key set)" : "sk-..."}
                      aria-invalid={openaiNeedsKey}
                      className={`su-profile-input ${
                        openaiNeedsKey ? "border-red-800" : ""
                      }`}
                    />
                    <div className="mt-2 flex items-center text-xs text-gray-400">
                      <span
                        className={`mr-2 inline-block h-2 w-2 rounded-full ${
                          hasOpenAIKey ? "bg-green-500" : "bg-gray-600"
                        }`}
                        aria-hidden="true"
                      />
                      {hasOpenAIKey ? "Key on file (hidden)" : "No key on file"}
                    </div>
                  </div>
                </div>

                <p className="text-xs text-gray-500">
                  Keys are stored securely using server-side secret storage. They
                  are never exposed in responses. Click &quot;SAVE SETTINGS&quot; above to save any new keys.
                </p>
              </div>
            </>
          ) : (
            <div
              className="relative"
              style={{
                backgroundColor: "rgba(0, 0, 0, 0.3)",
                border: "1px solid #374151",
                padding: "24px",
              }}
            >
              {/* Locked overlay content */}
              <div className="text-center">
                <div
                  className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full"
                  style={{ backgroundColor: "rgba(168, 85, 247, 0.1)" }}
                >
                  <svg
                    className="h-8 w-8 text-purple-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
                <h3 className="mb-2 text-lg font-semibold text-white">
                  Use Your Own API Keys
                </h3>
                <p className="mb-4 text-sm text-gray-400" style={{ maxWidth: "400px", margin: "0 auto 16px" }}>
                  Take control of your AI usage with custom OpenAI or Anthropic API keys.
                  Enjoy complete data privacy, no rate limits, and transparent costs.
                </p>
                <button
                  type="button"
                  onClick={() => setShowApiKeyUpgradeModal(true)}
                  className="su-btn-primary"
                  style={{
                    backgroundColor: "#A855F7",
                    borderColor: "#A855F7",
                  }}
                >
                  UNLOCK WITH PRO
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Contact Support Section */}
        <div className="su-section">
          <h2 className="su-text-section-heading">Contact Support</h2>
          <p className="su-text-subtitle" style={{ marginBottom: '24px' }}>
            Have feedback or need help? We&apos;d love to hear from you.
          </p>

          {!contactFormOpen ? (
            <button
              onClick={() => setContactFormOpen(true)}
              className="su-btn-primary"
            >
              Contact Support
            </button>
          ) : (
            <form onSubmit={handleContactSupport} className="su-settings-form">
              {/* Referral Source */}
              <div className="su-profile-field">
                <label className="su-profile-label">
                  How did you hear about us? *
                </label>
                <select
                  value={referralSource}
                  onChange={(e) => setReferralSource(e.target.value)}
                  className="su-profile-input"
                  required
                >
                  <option value="">Select an option</option>
                  <option value="Search Engine">Search Engine (Google, Bing, etc.)</option>
                  <option value="Social Media">Social Media (Twitter, LinkedIn, etc.)</option>
                  <option value="Friend/Colleague">Friend or Colleague</option>
                  <option value="Blog/Article">Blog or Article</option>
                  <option value="Advertisement">Advertisement</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Recommendation Rating */}
              <div className="su-profile-field">
                <label className="su-profile-label">
                  How likely are you to recommend SendUpdates.ai? (1-10) *
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={recommendationRating}
                    onChange={(e) => setRecommendationRating(Number(e.target.value))}
                    className="su-rating-slider"
                    required
                  />
                  <span className="su-rating-value">{recommendationRating}/10</span>
                </div>
              </div>

              {/* Improvements */}
              <div className="su-profile-field">
                <label className="su-profile-label">
                  What can we do better? *
                </label>
                <textarea
                  value={improvements}
                  onChange={(e) => setImprovements(e.target.value)}
                  placeholder="Share your feedback on how we can improve..."
                  className="su-profile-textarea"
                  rows={4}
                  required
                />
              </div>

              {/* Additional Details */}
              <div className="su-profile-field">
                <label className="su-profile-label">
                  Any other details you would like to add?
                </label>
                <textarea
                  value={additionalDetails}
                  onChange={(e) => setAdditionalDetails(e.target.value)}
                  placeholder="Optional: Share any additional thoughts or details..."
                  className="su-profile-textarea"
                  rows={3}
                />
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  type="submit"
                  disabled={contactFormLoading}
                  className="su-btn-primary"
                >
                  {contactFormLoading ? 'Sending...' : 'Send Message'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setContactFormOpen(false);
                    setReferralSource("");
                    setRecommendationRating(5);
                    setImprovements("");
                    setAdditionalDetails("");
                  }}
                  disabled={contactFormLoading}
                  className="su-btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Admin Section - Only visible to admin users */}
        {userRole === 'admin' && (
          <div className="su-section border-t border-gray-800 mt-8 pt-8">
            <h2 className="su-text-section-heading mb-4 text-purple-400">Platform Administration</h2>
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center justify-between w-full p-4 bg-gradient-to-r from-purple-900/30 to-purple-800/20 border border-purple-700/50 rounded-lg hover:from-purple-900/50 hover:to-purple-800/30 hover:border-purple-600 transition-all group"
            >
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-lg bg-purple-600/20 flex items-center justify-center mr-4 group-hover:bg-purple-600/30 transition-colors">
                  <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                </div>
                <div className="text-left">
                  <div className="font-semibold text-white group-hover:text-purple-300 transition-colors">Admin Dashboard</div>
                  <div className="text-sm text-gray-400">Manage platform settings, users, and system configuration</div>
                </div>
              </div>
              <ChevronRight />
            </button>
          </div>
        )}
      </div>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      <ApiKeyUpgradeModal
        isOpen={showApiKeyUpgradeModal}
        onClose={() => setShowApiKeyUpgradeModal(false)}
      />
    </div>
  );
};

export default Settings;
